import {
  Badge,
  Box,
  Card,
  Center,
  Container,
  Divider,
  Flex,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  BookIcon,
  FileTextIcon,
  MonitorIcon,
  SettingsIcon,
  UsersIcon,
  DatabaseIcon,
} from 'lucide-react';
import { ReactNode, useState } from 'react';

const JumboTitle = ({
  children,
  order = 1,
  fz = 'xl',
  ...otherProps
}: {
  children: ReactNode;
  order?: 1 | 2 | 3 | 4 | 5;
  fz?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any;
}) => {
  const FONT_WEIGHT_MAP: Record<string, number> = {
    xl: 800,
    lg: 800,
    md: 700,
    sm: 600,
    xs: 500,
  };

  // Font sizes matching Titanium's JumboTitle CSS classes
  const FONT_SIZE_MAP: Record<string, string> = {
    xl: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
    lg: 'clamp(2rem, 4vw + 1rem, 3.5rem)',
    md: 'clamp(1.75rem, 3vw + 1rem, 3rem)',
    sm: 'clamp(1.5rem, 2.5vw + 1rem, 2.5rem)',
    xs: 'clamp(1.25rem, 2vw + 1rem, 2rem)',
  };

  const { style: otherStyle, ...restProps } = otherProps;

  return (
    <Title
      fw={FONT_WEIGHT_MAP[fz]}
      lh={1}
      order={order}
      style={{
        fontSize: FONT_SIZE_MAP[fz],
        ...otherStyle,
      }}
      {...restProps}
    >
      {children}
    </Title>
  );
};

const Icon = ({ children }: { children: ReactNode }) => (
  <Card radius='xl' p='sm' withBorder>
    <Center>{children}</Center>
  </Card>
);

interface PricingItem {
  title: string;
  description: string;
  icon: ReactNode;
}

const PricingSection = () => {
  const [showMonthlyPricing, setShowMonthlyPricing] = useState(false);

  const price = showMonthlyPricing ? '$30' : '$20';
  const strikethroughPrice = showMonthlyPricing ? undefined : '$30';

  const baseItems: PricingItem[] = [
    {
      title: 'Case Management',
      description: 'From intake through case closing in one system',
      icon: (
        <Icon>
          <FileTextIcon size={21} />
        </Icon>
      ),
    },
    {
      title: 'Handle All Types of Fees',
      description: 'Hourly, fixed, subscription, contingency, insurance',
      icon: (
        <Icon>
          <MonitorIcon size={21} />
        </Icon>
      ),
    },
    {
      title: 'Run Your Business',
      description: 'AI assistant, trust ledger, management tools',
      icon: (
        <Icon>
          <UsersIcon size={21} />
        </Icon>
      ),
    },
  ];

  const annualOnlyItem: PricingItem = {
    title: 'Migration Support',
    description: 'Assistance migrating data from your old system',
    icon: (
      <Icon>
        <DatabaseIcon size={21} />
      </Icon>
    ),
  };

  const items = showMonthlyPricing ? baseItems : [...baseItems, annualOnlyItem];

  return (
    <Container
      id='pricing'
      bg='var(--mantine-color-body)'
      py={{
        base: 'calc(var(--mantine-spacing-lg) * 1)',
        xs: 'calc(var(--mantine-spacing-lg) * 1.5)',
        lg: 'calc(var(--mantine-spacing-lg) * 2)',
      }}
      fluid
    >
      <Container size='md'>
        <Stack align='center' gap='xs'>
          <JumboTitle
            order={2}
            fz='md'
            ta='center'
            style={{ textWrap: 'balance' }}
          >
            Simple, Transparent Pricing
          </JumboTitle>
          <Text c='dimmed' ta='center' fz='lg' style={{ textWrap: 'balance' }}>
            Choose the billing cycle that works best for you. Save when you pay
            annually.
          </Text>
          <Center>
            <SegmentedControl
              mt='md'
              size='lg'
              maw={320}
              radius='xl'
              data={['Monthly', 'Annual (Save 33%)']}
              value={showMonthlyPricing ? 'Monthly' : 'Annual (Save 33%)'}
              onChange={(value) => setShowMonthlyPricing(value === 'Monthly')}
            />
          </Center>
        </Stack>
      </Container>
      <Group
        mt={{
          base: 'calc(var(--mantine-spacing-lg) * 1.5)',
          lg: 'calc(var(--mantine-spacing-lg) * 2)',
        }}
        justify='center'
        gap='xl'
      >
        <Card
          radius='lg'
          shadow='lg'
          miw={{ base: '100%', sm: 350 }}
          maw={380}
          withBorder
        >
          <Group justify='space-between' align='start' mb='md'>
            <Box>
              <Icon>
                <UsersIcon size={21} />
              </Icon>
            </Box>
            <Box>
              <Badge variant='light' size='lg'>
                No Add-Ons Necessary
              </Badge>
            </Box>
          </Group>
          <Text fz='xl' fw='bold'>
            Comprehensive Plan
          </Text>
          <Text mb='md' c='dimmed'>
            Everything you need to manage your law firm.
          </Text>
          {/* {strikethroughPrice && (
            <Text c='dimmed' fz='lg' td='line-through' span>
              {strikethroughPrice}
            </Text>
          )} */}
          <Flex align='end' gap='xs'>
            <JumboTitle my={0} order={2} fz='xl' mb='sm'>
              {price}
            </JumboTitle>
            <Text c='dimmed' span fz='lg'>
              /user/month
            </Text>
          </Flex>
          <Card.Section my='md'>
            <Divider />
          </Card.Section>
          <Stack gap='sm'>
            {items.map((item) => (
              <Group key={item.title} gap='xs' align='flex-start' wrap='nowrap'>
                <Box style={{ flexShrink: 0 }}>{item.icon}</Box>
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={500} fz='sm'>
                    {item.title}
                  </Text>
                  <Text c='dimmed' fz='xs' style={{ wordBreak: 'break-word' }}>
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            ))}
          </Stack>
        </Card>
      </Group>
    </Container>
  );
};

export default PricingSection;
